import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { providersApi } from '../services/api';

const DEFAULT_WHY_JOIN_ITEMS = [
  { icon: 'credit_card', title: 'Keep 100%', description: 'No commission. Ever.' },
  { icon: 'check_circle', title: 'Free to join', description: 'Apply in 3 minutes.' },
  { icon: 'people', title: 'Owners nearby', description: 'Matched by location.' },
  { icon: 'chat', title: 'Built-in inbox', description: 'Chat & book in-app.' },
  { icon: 'calendar_today', title: 'Your schedule', description: 'You set availability.' },
  { icon: 'star', title: 'Pro features', description: 'Optional upgrades.' },
];

const DEFAULT_HOW_IT_WORKS_STEPS = [
  { title: 'Submit your application', description: 'Tell us about you and your services.' },
  { title: 'Get reviewed in 24–48h', description: 'Our team verifies every provider.' },
  { title: 'Start getting requests', description: 'Owners book you directly. You get paid.' },
];

const defaultConfig = () => ({
  badge: 'Now welcoming providers',
  headline: 'Turn your love for dogs into work.',
  subtitle:
    'Walkers, trainers, groomers, sitters & daycares — list your services and meet local owners.',
  commissionTitle: '0% commission, forever',
  commissionDesc: 'We never take a cut from your bookings.',
  whyJoinUsTitle: 'WHY JOIN US',
  whyJoinUsItems: DEFAULT_WHY_JOIN_ITEMS.map((item) => ({ ...item })),
  howItWorksTitle: 'HOW IT WORKS',
  howItWorksSteps: DEFAULT_HOW_IT_WORKS_STEPS.map((step) => ({ ...step })),
  ctaButton: 'Start your application',
  ctaSubtext: 'Free · Takes about 3 minutes',
});

const LOCALES = ['en', 'es', 'he'];
const LOCALE_LABELS = { en: 'EN', es: 'ES', he: 'HE' };

const ProviderLandingConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const [configs, setConfigs] = useState({
    en: defaultConfig(),
    es: defaultConfig(),
    he: defaultConfig(),
  });

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [en, es, he] = await Promise.all([
        providersApi.getLandingConfig('en'),
        providersApi.getLandingConfig('es'),
        providersApi.getLandingConfig('he'),
      ]);
      setConfigs({
        en: mergeWithDefault(en),
        es: mergeWithDefault(es),
        he: mergeWithDefault(he),
      });
    } catch (e) {
      console.error(e);
      setError('Failed to load landing config. Showing defaults.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // Merge API response into default so any missing keys use the default value
  const mergeWithDefault = (remote) => {
    if (!remote || typeof remote !== 'object') return defaultConfig();
    const base = defaultConfig();
    const merged = { ...base, ...remote };
    // Ensure array lengths match defaults
    if (!Array.isArray(merged.whyJoinUsItems) || merged.whyJoinUsItems.length !== 6) {
      merged.whyJoinUsItems = base.whyJoinUsItems;
    }
    if (!Array.isArray(merged.howItWorksSteps) || merged.howItWorksSteps.length !== 3) {
      merged.howItWorksSteps = base.howItWorksSteps;
    }
    return merged;
  };

  const currentLocale = LOCALES[activeTab];

  const updateField = (field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [currentLocale]: { ...prev[currentLocale], [field]: value },
    }));
  };

  const updateWhyJoinItem = (index, field, value) => {
    setConfigs((prev) => {
      const items = prev[currentLocale].whyJoinUsItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      return { ...prev, [currentLocale]: { ...prev[currentLocale], whyJoinUsItems: items } };
    });
  };

  const updateHowItWorksStep = (index, field, value) => {
    setConfigs((prev) => {
      const steps = prev[currentLocale].howItWorksSteps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      );
      return { ...prev, [currentLocale]: { ...prev[currentLocale], howItWorksSteps: steps } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await providersApi.upsertLandingConfig({
        locale: currentLocale,
        content: configs[currentLocale],
      });
      setSuccess(`Provider landing config saved for locale: ${currentLocale.toUpperCase()}`);
    } catch (e) {
      console.error(e);
      setError(`Failed to save landing config for ${currentLocale.toUpperCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const cfg = configs[currentLocale];

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Typography variant="h4" component="h1" gutterBottom>
        Provider Landing Page Config
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure the provider recruitment landing screen content shown in the app before providers
        submit their application.
      </Typography>

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={2}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={300}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ mb: 3 }}
          >
            {LOCALES.map((locale) => (
              <Tab key={locale} label={LOCALE_LABELS[locale]} />
            ))}
          </Tabs>

          <Stack spacing={3}>
            {/* Hero Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Hero Section
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Badge"
                  value={cfg.badge}
                  onChange={(e) => updateField('badge', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Headline"
                  value={cfg.headline}
                  onChange={(e) => updateField('headline', e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Subtitle"
                  value={cfg.subtitle}
                  onChange={(e) => updateField('subtitle', e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            </Paper>

            {/* Commission Highlight */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Commission Highlight
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Commission Title"
                  value={cfg.commissionTitle}
                  onChange={(e) => updateField('commissionTitle', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Commission Description"
                  value={cfg.commissionDesc}
                  onChange={(e) => updateField('commissionDesc', e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            </Paper>

            {/* Why Join Us */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Why Join Us
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Section Title"
                  value={cfg.whyJoinUsTitle}
                  onChange={(e) => updateField('whyJoinUsTitle', e.target.value)}
                  fullWidth
                />
                {cfg.whyJoinUsItems.map((item, index) => (
                  <Box key={index}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Item {index + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Icon"
                          value={item.icon}
                          onChange={(e) => updateWhyJoinItem(index, 'icon', e.target.value)}
                          fullWidth
                          size="small"
                          helperText="Material icon name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Title"
                          value={item.title}
                          onChange={(e) => updateWhyJoinItem(index, 'title', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Description"
                          value={item.description}
                          onChange={(e) => updateWhyJoinItem(index, 'description', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* How It Works */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                How It Works
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Section Title"
                  value={cfg.howItWorksTitle}
                  onChange={(e) => updateField('howItWorksTitle', e.target.value)}
                  fullWidth
                />
                {cfg.howItWorksSteps.map((step, index) => (
                  <Box key={index}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Step {index + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Title"
                          value={step.title}
                          onChange={(e) => updateHowItWorksStep(index, 'title', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <TextField
                          label="Description"
                          value={step.description}
                          onChange={(e) =>
                            updateHowItWorksStep(index, 'description', e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* CTA */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Call to Action
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="CTA Button Label"
                  value={cfg.ctaButton}
                  onChange={(e) => updateField('ctaButton', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="CTA Subtext"
                  value={cfg.ctaSubtext}
                  onChange={(e) => updateField('ctaSubtext', e.target.value)}
                  fullWidth
                />
              </Stack>
            </Paper>

            {/* Save */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={loadConfigs} disabled={saving}>
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : `Save ${currentLocale.toUpperCase()}`}
              </Button>
            </Box>
          </Stack>
        </>
      )}
    </>
  );
};

export default ProviderLandingConfig;
